import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';

import nexusAtsLogo from '@/assets/logos/nexus-ats-logo.png';
import elempleoLogo from '@/assets/logos/elempleo-logo.png';
import nexusAssessmentLogo from '@/assets/logos/nexus-assessment-logo.png';
import talentoSelectivoLogo from '@/assets/logos/talento-selectivo-logo.png';
import talentPayLogo from '@/assets/logos/talent-pay-logo.png';
import talentConnectLogo from '@/assets/logos/talent-connect-logo.png';
import kokoroLogo from '@/assets/logos/kokoro-logo.png';
import psigmaLogo from '@/assets/logos/psigma-logo.png';
import orienta360Logo from '@/assets/logos/orienta360-logo.png';
import nexusLanguagesLogo from '@/assets/logos/nexus-languages-logo.png';
import peopleLabsLogo from '@/assets/logos/people-labs-logo.png';
import agenteLogo from '@/assets/logos/employee-hub-logo.png';
import nexusLogo from '@/assets/nexus-logo.png';

interface ServiceCardData {
  logo: string;
  logoHeight: string;
  title: string;
  description: string;
  path: string;
  highlight?: boolean;
  additionalLogos?: { src: string; height: string }[];
}

const services: ServiceCardData[] = [
  {
    logo: talentConnectLogo,
    logoHeight: 'h-9',
    title: 'Talent Connect',
    description: 'Ferias de empleo virtuales para conectar con talento masivamente.',
    path: '/talent-connect',
  },
  {
    logo: nexusAtsLogo,
    logoHeight: 'h-6',
    title: 'Nexus ATS',
    description: 'Sistema de seguimiento de candidatos con IA. Publica en +17 job boards con un clic.',
    path: '/nexus-ats',
    highlight: true,
  },
  {
    logo: agenteLogo,
    logoHeight: 'h-10',
    title: 'Employee Hub+',
    description: 'Workforce Platform: jornada, vacaciones, expedientes y auditoría de personal en un solo lugar.',
    path: '/agente',
  },
  {
    logo: elempleoLogo,
    logoHeight: 'h-7',
    title: 'Elempleo.com',
    description: 'Acceso a la bolsa de empleo más grande de Costa Rica con +1M de candidatos.',
    path: '/elempleo',
  },
  {
    logo: nexusAssessmentLogo,
    logoHeight: 'h-7',
    title: 'Pruebas Psicométricas',
    description: 'Evaluaciones validadas científicamente para medir competencias y personalidad.',
    path: '/pruebas-psicometricas',
    additionalLogos: [
      { src: kokoroLogo, height: 'h-5' },
      { src: psigmaLogo, height: 'h-5' },
    ],
  },
  {
    logo: orienta360Logo,
    logoHeight: 'h-8',
    title: 'Orienta360',
    description: 'Orientación vocacional y perfil conductual para estudiantes y colaboradores.',
    path: '/orientacion-vocacional',
  },
  {
    logo: nexusLanguagesLogo,
    logoHeight: 'h-9',
    title: 'Nexus Languages',
    description: 'Evaluación de inglés con estándar MCER para procesos de contratación.',
    path: '/nexus-languages',
  },
  {
    logo: talentoSelectivoLogo,
    logoHeight: 'h-8',
    title: 'Reclutamiento y Selección',
    description: 'Headhunting especializado para posiciones estratégicas y de alto nivel.',
    path: '/reclutamiento-seleccion',
  },
  {
    logo: talentPayLogo,
    logoHeight: 'h-9',
    title: 'Encuesta Salarial',
    description: 'Datos actualizados del mercado para tomar decisiones de compensación informadas.',
    path: '/encuesta-salarial',
  },
  {
    logo: peopleLabsLogo,
    logoHeight: 'h-9',
    title: 'Encuesta de Clima y Cultura',
    description: 'Diagnóstico organizacional con IA para mejorar el clima y la cultura de su empresa.',
    path: '/encuesta-clima-cultura',
  },
];

const ServiceCard: React.FC<ServiceCardData> = ({
  logo,
  logoHeight,
  title,
  description,
  path,
  highlight,
  additionalLogos,
}) => {
  const navigate = useNavigate();

  return (
    <button
      onClick={() => navigate(path)}
      className={cn(
        'group relative text-left rounded-2xl p-6 md:p-7 transition-all duration-200 ease-out',
        'w-[280px] md:w-[300px] flex-shrink-0 snap-start',
        'border-[1.5px]',
        highlight
          ? 'bg-[#620AE2] text-white border-[#620AE2] shadow-xl hover:-translate-y-1'
          : 'bg-white text-foreground border-transparent shadow-sm hover:shadow-xl hover:-translate-y-1 hover:border-[#620AE2]/20',
      )}
    >
      <div
        className={cn(
          'min-h-[32px] mb-5 flex items-center gap-2 flex-wrap',
          highlight ? 'brightness-0 invert' : '',
        )}
      >
        <img
          src={logo}
          alt={title}
          className={cn(logoHeight, 'w-auto max-w-[120px] object-contain object-left')}
          loading="lazy"
          decoding="async"
        />
        {additionalLogos?.map((al, idx) => (
          <img
            key={idx}
            src={al.src}
            alt={`${title} partner ${idx + 1}`}
            className={cn(al.height, 'w-auto max-w-[60px] object-contain object-left')}
            loading="lazy"
            decoding="async"
          />
        ))}
      </div>

      <h3
        className={cn(
          'text-lg md:text-xl font-bold mb-2',
          highlight ? 'text-white' : 'text-foreground',
        )}
      >
        {title}
      </h3>

      <p
        className={cn(
          'text-sm leading-relaxed mb-5',
          highlight ? 'text-white/85' : 'text-muted-foreground',
        )}
      >
        {description}
      </p>

      <div
        className={cn(
          'flex items-center text-sm font-semibold',
          highlight ? 'text-white' : 'text-[#620AE2]',
        )}
      >
        Conocer más
        <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-1" />
      </div>
    </button>
  );
};

const EmpresasGrid: React.FC = () => {
  return (
    <section id="empresas" className="bg-[#F5F5F7]" style={{ paddingTop: "80px", paddingBottom: "80px" }}>
      <div className="container mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-left mb-10 md:mb-14">
          <span className="text-[#620AE2] font-semibold text-xs md:text-sm uppercase tracking-[0.2em]">
            Para Empresas
          </span>
          <h2
            className="font-exo mt-3 mb-3"
            style={{ fontSize: '40px', color: '#1A1A2E', fontWeight: 700, lineHeight: 1.1 }}
          >
            Soluciones integrales de talento humano
          </h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-2xl">
            Desde la publicación de ofertas hasta la contratación final.
          </p>
        </div>

        <div className="-mx-4 md:-mx-6 lg:-mx-8 px-4 md:px-6 lg:px-8 overflow-x-auto snap-x snap-mandatory scrollbar-hide">
          <div className="flex gap-5 md:gap-6 pb-4">
            {services.map((s) => (
              <ServiceCard key={s.title} {...s} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default EmpresasGrid;
